const CHANNEL_LABEL_KEYS = {
  email: 'common.channel_email',
  sms: 'common.channel_sms',
  whatsapp: 'common.channel_whatsapp',
  push: 'common.channel_app_push',
  web_push: 'common.channel_web_push',
};

const OPERATOR_LABEL_KEYS = {
  '>=': 'audiences.audience_conditions_bigger_than2',
  '>': 'audiences.audience_conditions_bigger_than',
  '<=': 'audiences.audience_conditions_less_than2',
  '<': 'audiences.audience_conditions_less_than',
  '=': 'audiences.audience_conditions_equal',
  '!=': 'audiences.audience_conditions_not_equal',
  in: 'audiences.audience_conditions_belongs',
  not_in: 'audiences.audience_conditions_not_belongs',
  like: 'audiences.audience_conditions_contains',
  not_like: 'audiences.audience_conditions_no_contains',
  empty: 'audiences.audience_conditions_empty',
  not_empty: 'audiences.audience_conditions_not_empty',
  last: 'journeys.journey_workflow_components_condition_simple_condition_at_least',
  before_last: 'journeys.journey_workflow_components_condition_simple_condition_at_before_least',
  day: 'audiences.audience_conditions_date_day',
  day_of_week: 'audiences.audience_conditions_date_day_week',
  month: 'audiences.audience_conditions_date_month',
  year: 'audiences.audience_conditions_date_year',
  reachable: 'audiences.audience_conditions_reachable',
  not_reachable: 'audiences.audience_conditions_not_reachable',
  clicked: 'audiences.audience_conditions_channel_status_clicked',
  highest: 'audiences.audience_conditions_affinity_highest',
  top_3: 'audiences.audience_conditions_affinity_top_3',
  top_5: 'audiences.audience_conditions_affinity_top_5',
  top_10: 'audiences.audience_conditions_affinity_top_10',
};

const LIKEHOOD_SUMMARY_VALUE_KEYS = {
  high: 'audiences.audience_conditions_summary_likehood_value_high',
  normal: 'audiences.audience_conditions_summary_likehood_value_normal',
  low: 'audiences.audience_conditions_summary_likehood_value_low',
};

const LIFECYCLE_SUMMARY_VALUE_KEYS = {
  active: 'audiences.audience_conditions_summary_lifecycle_value_active',
  inactive: 'audiences.audience_conditions_summary_lifecycle_value_inactive',
  churn: 'audiences.audience_conditions_summary_lifecycle_value_churn',
  potential_buyer: 'audiences.audience_conditions_summary_lifecycle_value_potential_buyer',
  visitor: 'audiences.audience_conditions_summary_lifecycle_value_visitor',
  silent_visitor: 'audiences.audience_conditions_summary_lifecycle_value_silent_visitor',
};

function bold(text) {
  if (text == null || text === '') return '';
  return `<strong>${text}</strong>`;
}

function labelOf(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return value.label || value.name || value.value || value.id || '';
}

function channelLabel(term, t) {
  const id = term?.id || term;
  if (term?.label) return term.label;
  const key = CHANNEL_LABEL_KEYS[id];
  return key ? t(key) : String(id || '');
}

function broadcastLabel(node, context) {
  return node.broadcastName
    || node.broadcastLabel
    || context?.broadcasts?.[node.broadcastId]
    || '';
}

function journeyLabel(node, context) {
  return node.journeyName
    || node.journeyLabel
    || context?.journeys?.[node.journeyId]
    || '';
}

function findNestedChild(node, field) {
  return node?.term?.children?.find((child) => child.field === field);
}

function operatorLabel(operator, t) {
  if (operator == null || operator === '') return '';
  if (typeof operator === 'object') {
    if (operator.label) return operator.label;
    return operatorLabel(operator.id, t);
  }
  const key = OPERATOR_LABEL_KEYS[operator];
  return key ? t(key) : String(operator);
}

function predictiveTermLabel(term, t, valueKeys) {
  const id = term?.id || term;
  const key = valueKeys[id];
  if (key) return t(key);
  return labelOf(term);
}

function isAnyProductField(field) {
  if (field == null || field === '') return false;
  if (field === 'any') return true;
  if (typeof field === 'string') return field === 'any';
  return field.column_name === 'any' || field.type === 'any';
}

function termValuesLabel(term) {
  if (Array.isArray(term)) {
    return term.map(labelOf).filter(Boolean).join(', ');
  }
  return labelOf(term);
}

function summarizeProductCondition(child, t) {
  const field = child?.field;
  if (!field || isAnyProductField(field)) return null;

  const propName = labelOf(field);
  const operator = operatorLabel(child.operator, t);

  if (child.operator === 'empty' || child.operator === 'not_empty') {
    return `${bold(propName)} ${bold(operator)}`;
  }

  const value = termValuesLabel(child.term);
  if (!value) return `${bold(propName)} ${bold(operator)}`;
  return `${bold(propName)} ${bold(operator)} ${bold(value)}`;
}

function summarizeProductFilters(query, t) {
  const children = query?.children || [];
  if (!children.length) return null;

  const hasAnyOnly = children.every((child) => isAnyProductField(child.field));
  if (hasAnyOnly) return t('audiences.audience_conditions_summary_product_any');

  const conditions = children
    .map((child) => summarizeProductCondition(child, t))
    .filter(Boolean);

  if (!conditions.length) return t('audiences.audience_conditions_summary_product_any');
  if (conditions.length === 1) return conditions[0];

  const connector = query?.type === 'or'
    ? ` ${t('audiences.audience_conditions_summary_connector_or_word')} `
    : ` ${t('audiences.audience_conditions_summary_connector_and_word')} `;

  return `(${conditions.join(connector)})`;
}

function isAnyLiveShoppingField(field) {
  if (field == null || field === '') return false;
  if (field === 'any') return true;
  if (typeof field === 'string') return field === 'any';
  return field.id === 'any' || field.type === 'any';
}

const LIVE_SHOPPING_FIELD_LABEL_KEYS = {
  any: 'audiences.audience_conditions_fields_live_Shopping_invitations_any',
  invitation_date: 'audiences.audience_conditions_fields_live_Shopping_invitations_date',
  invitation_campaign: 'audiences.audience_conditions_fields_live_Shopping_invitations_campaign',
  event_date: 'audiences.audience_conditions_fields_live_Shopping_events_date',
  event_campaign: 'audiences.audience_conditions_fields_live_Shopping_invitations_campaign',
  event_host: 'audiences.audience_conditions_fields_live_Shopping_events_host',
  event_status: 'audiences.audience_conditions_fields_live_Shopping_events_status',
};

function liveShoppingFieldLabel(field, t) {
  if (field?.label) return field.label;
  const id = typeof field === 'string' ? field : field?.id;
  const key = LIVE_SHOPPING_FIELD_LABEL_KEYS[id];
  return key ? t(key) : labelOf(field);
}

function liveShoppingFieldType(field) {
  if (typeof field === 'object' && field?.type) return field.type;
  const id = typeof field === 'string' ? field : field?.id;
  if (id === 'invitation_campaign' || id === 'event_campaign') return 'campaigns';
  if (id === 'event_host') return 'hosts';
  if (id === 'event_status') return 'eventStatus';
  if (id === 'invitation_date' || id === 'event_date') return 'date';
  if (id === 'any') return 'any';
  return null;
}

function summarizeLiveShoppingListCondition(child, t) {
  const propName = liveShoppingFieldLabel(child.field, t);
  const terms = Array.isArray(child.term) ? child.term : [child.term];
  const labels = joinLabels(terms, t);
  const prefixKey = child.operator === 'not_in'
    ? 'audiences.audience_conditions_summary_live_shopping_none_of'
    : 'audiences.audience_conditions_summary_live_shopping_one_of';

  return `${bold(propName)} ${t(prefixKey)} ${labels}`;
}

function summarizeLiveShoppingCondition(child, t) {
  const field = child?.field;
  if (!field || isAnyLiveShoppingField(field)) return null;

  const fieldType = liveShoppingFieldType(field);
  if (fieldType === 'campaigns' || fieldType === 'hosts' || fieldType === 'eventStatus') {
    return summarizeLiveShoppingListCondition(child, t);
  }
  if (fieldType === 'date') {
    const propName = liveShoppingFieldLabel(field, t);
    const operator = operatorLabel(child.operator, t);
    const value = labelOf(child.term);
    return `${bold(propName)} ${bold(operator)} ${bold(value)}`;
  }

  const propName = liveShoppingFieldLabel(field, t);
  const operator = operatorLabel(child.operator, t);
  const value = termValuesLabel(child.term);
  if (!value) return `${bold(propName)} ${bold(operator)}`;
  return `${bold(propName)} ${bold(operator)} ${bold(value)}`;
}

function summarizeLiveShoppingFilters(query, t) {
  const children = query?.children || [];
  if (!children.length) return null;

  const anyLabel = t('audiences.audience_conditions_fields_live_Shopping_invitations_any');
  const hasAnyOnly = children.every((child) => isAnyLiveShoppingField(child.field));
  if (hasAnyOnly) return anyLabel;

  const conditions = children
    .map((child) => summarizeLiveShoppingCondition(child, t))
    .filter(Boolean);

  if (!conditions.length) return anyLabel;
  if (conditions.length === 1) return conditions[0];

  const connector = query?.type === 'or'
    ? ` ${t('audiences.audience_conditions_summary_connector_or_word')} `
    : ` ${t('audiences.audience_conditions_summary_connector_and_word')} `;

  return `(${conditions.join(connector)})`;
}

function summarizeSimpleTimesClause(node, t) {
  const count = node.times?.value;
  const operator = operatorLabel(node.times?.operator, t);
  const timesWord = Number(count) === 1
    ? t('audiences.audience_conditions_at_least_time')
    : t('audiences.audience_conditions_at_least_times');

  return [
    t('audiences.audience_conditions_summary_a_quantity'),
    bold(operator),
    bold(count),
    `${bold(timesWord)}.`,
  ].join(' ');
}

function summarizeLiveShoppingInteraction(node, fieldDef, t) {
  const actionKey = `audiences.audience_conditions_summary_action_${node.field}`;
  const action = t(actionKey);
  const actionText = action !== actionKey
    ? action
    : (fieldDef?.label || node.field);
  const anyLabel = t('audiences.audience_conditions_fields_live_Shopping_invitations_any');
  const filterPart = summarizeLiveShoppingFilters(node.term, t);
  const isAny = filterPart === anyLabel;

  if (isAny) {
    return [
      actionText,
      bold(filterPart),
      summarizeSimpleTimesClause(node, t),
    ].join(' ');
  }

  return [
    actionText,
    t('audiences.audience_conditions_summary_product_with'),
    filterPart,
    summarizeSimpleTimesClause(node, t),
  ].join(' ');
}

function summarizeFeedbackValuePart(valueChild, fieldType, t) {
  const operator = operatorLabel(valueChild.operator, t);
  const value = fieldType === 'feedback_nps'
    ? bold(String(valueChild.term))
    : bold(labelOf(valueChild.term));

  return [
    t('audiences.audience_conditions_summary_feedback_with_value'),
    bold(operator),
    value,
  ].join(' ');
}

function summarizeFeedbackContentPart(contentChild, t) {
  const labels = joinLabels(contentChild.term, t);
  const prefixKey = contentChild.operator === 'not_in'
    ? 'audiences.audience_conditions_summary_feedback_content_none_of'
    : 'audiences.audience_conditions_summary_feedback_content_one_of';

  return [t(prefixKey), labels].join(' ');
}

function summarizeFeedbackInteraction(node, fieldDef, t) {
  const valueChild = findNestedChild(node, 'value');
  const contentChild = findNestedChild(node, 'content');

  const actionKey = `audiences.audience_conditions_summary_action_${node.field}`;
  const action = t(actionKey);
  const actionText = action !== actionKey
    ? action
    : (fieldDef?.label || node.field);

  const parts = [actionText];

  if (valueChild?.term != null && valueChild.term !== '') {
    parts.push(summarizeFeedbackValuePart(valueChild, node.field, t));
  }

  if (contentChild?.term?.length) {
    parts.push(summarizeFeedbackContentPart(contentChild, t));
  }

  if (node.times?.value) {
    parts.push(summarizeTimesClause(node, t));
  }

  return parts.join(' ');
}

function summarizeTimesClause(node, t) {
  const count = node.times?.value;
  const operator = operatorLabel(node.times?.operator, t);
  const timesWord = Number(count) === 1
    ? t('audiences.audience_conditions_at_least_time')
    : t('audiences.audience_conditions_at_least_times');
  const frequency = node.times?.frequency;
  const period = frequency
    ? t('audiences.audience_conditions_summary_in_last_n_days', { days: frequency })
    : t('audiences.audience_conditions_input_num_last_days');

  return [
    t('audiences.audience_conditions_summary_a_quantity'),
    bold(operator),
    bold(count),
    bold(timesWord),
    bold(period) + '.',
  ].join(' ');
}

function joinLabels(items, t, resolveLabel = labelOf) {
  const labels = (items || [])
    .map((item) => resolveLabel(item))
    .filter(Boolean);

  if (!labels.length) return '';
  if (labels.length === 1) return bold(labels[0]);

  const connector = ` ${t('audiences.audience_conditions_summary_connector_or_word')} `;
  return labels.map(bold).join(connector);
}

function resolveBroadcastLabel(item, context) {
  return labelOf(item) || context?.broadcasts?.[item?.id || item] || '';
}

function summarizeEngagementBroadcastPart(broadcastChild, context, t) {
  const terms = broadcastChild?.term || [];
  if (!terms.length) {
    return [
      t('audiences.audience_conditions_summary_preposition_from'),
      bold(t('audiences.audience_conditions_summary_any_broadcast')),
    ].join(' ');
  }

  const labels = joinLabels(terms, t, (item) => resolveBroadcastLabel(item, context));
  const prefixKey = broadcastChild.operator === 'not_in'
    ? 'audiences.audience_conditions_summary_broadcast_none_of'
    : 'audiences.audience_conditions_summary_broadcast_one_of';

  return [
    t('audiences.audience_conditions_summary_preposition_from'),
    t(prefixKey),
    labels,
  ].join(' ');
}

function summarizeEngagementChannelPart(channelChild, t) {
  const terms = channelChild?.term || [];
  if (!terms.length) {
    return [
      t('audiences.audience_conditions_summary_preposition_on'),
      bold(t('audiences.audience_conditions_summary_any_channel')),
    ].join(' ');
  }

  const labels = joinLabels(terms, t, (item) => channelLabel(item, t));
  const prefixKey = channelChild.operator === 'not_in'
    ? 'audiences.audience_conditions_summary_channel_none_of'
    : 'audiences.audience_conditions_summary_channel_one_of';

  return [
    t('audiences.audience_conditions_summary_preposition_on'),
    t(prefixKey),
    labels,
  ].join(' ');
}

function summarizeInteractionBroadcastPart(broadcastChild, context, t) {
  const terms = broadcastChild?.term || [];
  if (!terms.length) {
    return bold(t('audiences.audience_conditions_summary_one_broadcast'));
  }

  const labels = joinLabels(terms, t, (item) => resolveBroadcastLabel(item, context));
  const prefixKey = broadcastChild.operator === 'not_in'
    ? 'audiences.audience_conditions_summary_broadcast_none_of'
    : 'audiences.audience_conditions_summary_broadcast_one_of';

  return [t(prefixKey), labels].join(' ');
}

function summarizeRfmCluster(node, t) {
  const clusters = joinLabels(node.term, t);
  const prefixKey = node.operator === 'not_in'
    ? 'audiences.audience_conditions_summary_rfm_not_in'
    : 'audiences.audience_conditions_summary_rfm_in';

  return [
    t(prefixKey),
    `${clusters}.`,
  ].join(' ');
}

function summarizeContactLists(node, t) {
  const lists = joinLabels(node.term, t);
  const prefixKey = node.operator === 'not_in'
    ? 'audiences.audience_conditions_summary_contact_list_not_in'
    : 'audiences.audience_conditions_summary_contact_list_in';

  return [
    t(prefixKey),
    `${lists}.`,
  ].join(' ');
}

const SEARCH_FIELD_LABEL_KEYS = {
  text: 'audiences.audience_conditions_search_field_text',
  type: 'audiences.audience_conditions_search_field_type',
  status: 'audiences.audience_conditions_search_field_status',
};

// List values are stored bare ({ id }), so their labels are resolved here.
const SEARCH_VALUE_LABEL_KEYS = {
  type: {
    text: 'search_list.text',
    image: 'search_list.image',
    voice: 'search_list.voice',
  },
  status: {
    generated: 'audiences.audience_conditions_search_status_generated',
    clicked: 'audiences.audience_conditions_search_status_clicked',
    empty: 'audiences.audience_conditions_search_status_empty',
  },
};

function searchValuesLabel(columnName, term, t) {
  const valueKeys = SEARCH_VALUE_LABEL_KEYS[columnName];
  if (!valueKeys) return termValuesLabel(term);

  return (Array.isArray(term) ? term : [term])
    .map((value) => {
      const key = valueKeys[String(value?.id ?? value)];
      return key ? t(key) : labelOf(value);
    })
    .filter(Boolean)
    .join(', ');
}

function summarizeSearchCondition(child, t) {
  const columnName = typeof child?.field === 'string' ? child.field : child?.field?.column_name;
  const labelKey = SEARCH_FIELD_LABEL_KEYS[columnName];
  // "any" carries no filter, so it contributes nothing to the sentence.
  if (!labelKey) return null;

  const value = searchValuesLabel(columnName, child.term, t);
  if (!value) return null;

  return `${bold(t(labelKey))} ${bold(operatorLabel(child.operator, t))} ${bold(value)}`;
}

function summarizeSearchFilters(query, t) {
  const conditions = (query?.children || [])
    .map((child) => summarizeSearchCondition(child, t))
    .filter(Boolean);

  if (!conditions.length) return null;
  if (conditions.length === 1) return conditions[0];

  const connector = query?.type === 'or'
    ? ` ${t('audiences.audience_conditions_summary_connector_or_word')} `
    : ` ${t('audiences.audience_conditions_summary_connector_and_word')} `;

  return `(${conditions.join(connector)})`;
}

function summarizeSearchInteraction(node, fieldDef, t) {
  const actionKey = `audiences.audience_conditions_summary_action_${node.field}`;
  const action = t(actionKey);
  const actionText = action !== actionKey
    ? action
    : (fieldDef?.label || node.field);

  const filterPart = summarizeSearchFilters(node.term, t);
  const timesClause = summarizeTimesClause(node, t);

  // "any search" has no filter, so the sentence goes straight to the count.
  return filterPart
    ? [actionText, `${t('audiences.audience_conditions_summary_product_with')} ${filterPart},`, timesClause].join(' ')
    : [actionText, timesClause].join(' ');
}

function summarizeProductsInteraction(node, fieldDef, t) {
  const actionKey = `audiences.audience_conditions_summary_action_${node.field}`;
  const action = t(actionKey);
  const actionText = action !== actionKey
    ? action
    : (fieldDef?.label || node.field);
  const filterPart = summarizeProductFilters(node.term, t);
  const isAnyItem = filterPart === t('audiences.audience_conditions_summary_product_any');

  if (isAnyItem) {
    return [
      actionText,
      bold(filterPart),
      summarizeTimesClause(node, t),
    ].join(' ');
  }

  return [
    actionText,
    t('audiences.audience_conditions_summary_product_with'),
    filterPart,
    summarizeTimesClause(node, t),
  ].join(' ');
}

export function buildAudienceFieldMap(fields) {
  const map = {};
  (fields || []).forEach((group) => {
    if (Array.isArray(group.values)) {
      group.values.forEach((field) => {
        if (field?.value != null) map[field.value] = field;
      });
    }
    if (group.value != null) {
      map[group.value] = {
        ...group,
        value: group.value,
        label: group.label || group.group,
      };
    }
  });
  return map;
}

export function collectRuleNodes(rules) {
  const nodes = [];
  if (!rules?.children?.length) return nodes;

  rules.children.forEach((child) => {
    if (child?.group) {
      nodes.push(...collectRuleNodes(child));
    } else if (child?.field) {
      nodes.push(child);
    }
  });

  return nodes;
}

function nodeNeedsBroadcastLookup(node, broadcasts) {
  if (node.broadcastId && !node.broadcastLabel && !node.broadcastName && !broadcasts[node.broadcastId]) {
    return true;
  }

  const broadcastChild = node.term?.children?.find((child) => child.field === 'broadcast');
  return (broadcastChild?.term || []).some((item) => {
    const id = item?.id || item;
    return id && !labelOf(item) && !broadcasts[id];
  });
}

function nodeNeedsJourneyLookup(node, journeys) {
  return !!(node.journeyId && !node.journeyLabel && !node.journeyName && !journeys[node.journeyId]);
}

export async function enrichAudienceSummaryContext(rules, context, loaders = {}) {
  const broadcasts = { ...(context.broadcasts || {}) };
  const journeys = { ...(context.journeys || {}) };
  const nodes = collectRuleNodes(rules);

  const needsBroadcasts = nodes.some((node) => nodeNeedsBroadcastLookup(node, broadcasts));
  const needsJourneys = nodes.some((node) => nodeNeedsJourneyLookup(node, journeys));

  const tasks = [];

  if (needsBroadcasts && loaders.loadBroadcasts) {
    tasks.push(
      loaders.loadBroadcasts().then((items) => {
        (items || []).forEach((item) => {
          const id = item?._id || item?.id;
          if (id) broadcasts[id] = item.name || item.label;
        });
      }),
    );
  }

  if (needsJourneys && loaders.loadJourneys) {
    tasks.push(
      loaders.loadJourneys().then((items) => {
        (items || []).forEach((item) => {
          const id = item?._id || item?.id;
          if (id) journeys[id] = item.name || item.label;
        });
      }),
    );
  }

  await Promise.all(tasks);

  return { ...context, broadcasts, journeys };
}

function summarizeInputNum(node, fieldDef, t) {
  const count = node.times?.value;
  const operator = operatorLabel(node.times?.operator, t);
  const timesWord = Number(count) === 1
    ? t('audiences.audience_conditions_at_least_time')
    : t('audiences.audience_conditions_at_least_times');
  const actionKey = `audiences.audience_conditions_summary_action_${node.field}`;
  const action = t(actionKey);
  const actionText = action !== actionKey
    ? action
    : (fieldDef?.label || node.field);

  return [
    actionText,
    t('audiences.audience_conditions_summary_a_quantity'),
    bold(operator),
    bold(count),
    bold(timesWord),
    `${t('audiences.audience_conditions_input_num_last_days')}.`,
  ].join(' ');
}

function summarizeAffinity(node, fieldDef, t) {
  const property = labelOf(node.affinity_type);
  const value = labelOf(node.term);
  const operatorId = typeof node.operator === 'object' ? node.operator?.id : node.operator;
  const operator = operatorLabel(node.operator, t);
  const isTopOperator = ['top_3', 'top_5', 'top_10'].includes(operatorId);

  if (isTopOperator) {
    return [
      bold(property),
      bold(value),
      t('audiences.audience_conditions_summary_affinity_is'),
      `${bold(operator)}.`,
    ].filter(Boolean).join(' ');
  }

  return [
    t('audiences.audience_conditions_summary_affinity_prefix'),
    bold(property),
    bold(value),
    `${bold(operator)}.`,
  ].filter(Boolean).join(' ');
}

function summarizeLikehoodPurchase(node, t) {
  const value = predictiveTermLabel(node.term, t, LIKEHOOD_SUMMARY_VALUE_KEYS);

  return [
    t('audiences.audience_conditions_summary_likehood'),
    bold(value) + '.',
  ].join(' ');
}

function summarizeCustomerCycleStatus(node, t) {
  const value = predictiveTermLabel(node.term, t, LIFECYCLE_SUMMARY_VALUE_KEYS);

  return [
    t('audiences.audience_conditions_summary_lifecycle'),
    bold(value) + '.',
  ].join(' ');
}

function summarizeChannelReachability(node, t) {
  const channel = channelLabel(node.term, t);
  const isReachable = node.operator !== 'not_reachable';
  const status = t(isReachable
    ? 'audiences.audience_conditions_reachable'
    : 'audiences.audience_conditions_not_reachable').toLowerCase();

  return [
    t('audiences.audience_conditions_summary_are'),
    bold(status),
    t('audiences.audience_conditions_channel_reachability_in').toLowerCase(),
    `${bold(channel)}.`,
  ].join(' ');
}

function summarizeNotificationUrlClicked(node, context, t, journey = false) {
  const name = journey ? journeyLabel(node, context) : broadcastLabel(node, context);
  const url = node.term;
  const action = bold(t('audiences.audience_conditions_summary_click_action'));
  const anyUrlLabel = bold(t('audiences.notification_url_clicked_any_url').toLowerCase());

  const entityPart = name
    ? `${t(journey ? 'audiences.audience_conditions_summary_entity_journey' : 'audiences.audience_conditions_summary_entity_broadcast')} ${bold(`"${name}"`)}`
    : bold(t(journey ? 'audiences.audience_conditions_summary_click_on_any_journey' : 'audiences.audience_conditions_summary_click_on_any_broadcast'));

  if (!url) {
    return [
      t('audiences.audience_conditions_summary_clicked'),
      action,
      entityPart,
      `(${anyUrlLabel}).`,
    ].join(' ');
  }

  return [
    t('audiences.audience_conditions_summary_clicked'),
    action,
    entityPart,
    t('audiences.audience_conditions_summary_towards_url'),
    `${bold(url)}.`,
  ].join(' ');
}

function summarizeChannelEngagement(node, fieldDef, context, t) {
  const statusChild = findNestedChild(node, 'status');
  const broadcastChild = findNestedChild(node, 'broadcast');
  const channelChild = findNestedChild(node, 'channel');
  const status = labelOf(statusChild?.term) || t('audiences.audience_conditions_channel_status_received');

  const parts = [
    bold(status),
    t('audiences.audience_conditions_summary_messages'),
  ];

  if (broadcastChild?.term?.length) {
    parts.push(summarizeEngagementBroadcastPart(broadcastChild, context, t));
  }

  if (channelChild?.term?.length) {
    parts.push(summarizeEngagementChannelPart(channelChild, t));
  }

  parts.push(summarizeTimesClause(node, t));

  return parts.join(' ');
}

function summarizeInteractionChannelEngagement(node, fieldDef, context, t) {
  const dateChild = findNestedChild(node, 'date');
  const statusChild = findNestedChild(node, 'status');
  const broadcastChild = findNestedChild(node, 'broadcast');
  const channelChild = findNestedChild(node, 'channel');

  const actionKey = `audiences.audience_conditions_summary_action_${node.field}`;
  const action = t(actionKey);
  const actionText = action !== actionKey
    ? action
    : (fieldDef?.label || node.field);
  const status = (labelOf(statusChild?.term)
    || t('audiences.audience_conditions_interaction_channel_status_received')).toLowerCase();

  const contextParts = [];
  const days = dateChild?.term;

  if (days != null) {
    const dayWord = Number(days) === 1 ? t('common.day') : t('common.days');
    contextParts.push(
      t('audiences.audience_conditions_summary_after_n_days'),
      bold(String(days)),
      bold(dayWord),
      t('audiences.audience_conditions_summary_of_having'),
    );
  } else {
    contextParts.push(t('audiences.audience_conditions_summary_after_having_once'));
  }

  contextParts.push(
    bold(status),
    summarizeInteractionBroadcastPart(broadcastChild, context, t),
  );

  if (channelChild?.term?.length) {
    contextParts.push(summarizeEngagementChannelPart(channelChild, t));
  }

  const sentence = [actionText, contextParts.join(' ')].join(' ');

  if (node.times?.value) {
    return [sentence, summarizeTimesClause(node, t)].join(' ');
  }

  return `${sentence}.`;
}

function summarizeListField(node, fieldDef, t) {
  const values = (node.term || [])
    .map((item) => labelOf(item))
    .filter(Boolean)
    .join(', ');
  const operator = operatorLabel(node.operator, t);

  return [
    `${bold(fieldDef?.label || node.field)}:`,
    operator ? bold(operator) : t('audiences.audience_conditions_summary_belong_to'),
    `${bold(values)}.`,
  ].join(' ');
}

function summarizeCustomerField(node, t) {
  const fieldName = labelOf(node.customer_field);
  const operator = operatorLabel(node.operator, t);
  const value = Array.isArray(node.term)
    ? node.term.map(labelOf).filter(Boolean).join(', ')
    : labelOf(node.term);

  if (node.operator === 'empty' || node.operator === 'not_empty') {
    return [
      t('audiences.audience_conditions_summary_property'),
      bold(fieldName),
      `${bold(operator)}.`,
    ].join(' ');
  }

  return [
    t('audiences.audience_conditions_summary_property'),
    bold(fieldName),
    bold(operator),
    `${bold(value)}.`,
  ].join(' ');
}


function summarizeNestedInteraction(node, fieldDef, t) {
  const count = node.times?.value;
  const frequency = node.times?.frequency;
  const timesWord = Number(count) === 1
    ? t('audiences.audience_conditions_at_least_time')
    : t('audiences.audience_conditions_at_least_times');
  const frequencySuffix = frequency
    ? ` ${t('audiences.audience_conditions_summary_nested_interaction_frequency_suffix', { frequency })}`
    : '';

  return [
    `${bold(fieldDef?.label || node.field)}:`,
    t('audiences.audience_conditions_at_least').toLowerCase(),
    bold(count),
    bold(timesWord) + frequencySuffix + '.',
  ].join(' ');
}

function isEngagementQueryValid(query, times) {
  let isOk = true;
  (query?.children || []).forEach((c) => {
    isOk = isOk && c.operator && c.term;
    if (c.field === 'broadcast' || c.field === 'channel') {
      isOk = isOk && c.term.length > 0;
    }
  });
  return isOk && times?.operator && times?.value;
}

function isNestedChildrenValid(node, isAnyField) {
  let isOk = node.term?.children?.length > 0;
  node.term?.children?.forEach((c) => {
    isOk = isAnyField(c.field) ? isOk : isOk && (c.field && c.operator && c.term);
  });
  return isOk;
}

function isProductsChildrenValid(node) {
  let isOk = node.term?.children?.length > 0;
  node.term?.children?.forEach((c) => {
    const termOK = c.operator === 'empty' || c.operator === 'not_empty'
      ? true
      : c.term && ((Array.isArray(c.term) && c.term.length > 0) || !Array.isArray(c.term));
    isOk = isAnyProductField(c.field) ? isOk : isOk && (c.field && c.operator && termOK);
  });
  return isOk;
}

function isCustomerFieldValid(node) {
  const customerField = node.customer_field;
  if (!node.field || !customerField || !node.operator) return false;

  if (customerField.type === 'date') {
    if (node.operator === 'last' || node.operator === 'before_last') {
      return !!(node.frequency && node.term);
    }
    if (node.operator === 'empty' || node.operator === 'not_empty') {
      return true;
    }
    if (node.operator === 'day') {
      return node.term && node.term.length > 0 && node.term.length <= 31;
    }
    if (node.operator === 'year') {
      return node.term && node.term.length > 0 && node.term.length <= 4;
    }
    if (node.operator) {
      return node.term != null && node.term !== '';
    }
    return false;
  }

  if (node.operator === 'empty' || node.operator === 'not_empty') {
    return true;
  }
  return node.term != null && node.term !== '';
}

export function isAudienceNodeValid(node, fieldMap) {
  if (!node?.field) return false;

  const fieldDef = fieldMap[node.field];
  const component = fieldDef?.term?.component;
  if (!component) return false;

  switch (component) {
    case 'view_page_component':
      return !!(node.field && node.operator && node.term?.length > 0
        && node.times?.operator && node.times?.value);
    case 'visit_url':
      return !!(node.field && node.operator && node.term
        && node.times?.operator && node.times?.value);
    case 'input_num':
      return !!(node.field && node.times?.operator && node.times?.value);
    case 'purchase_value':
      return !!(node.field && node.operator && node.term
        && node.times?.operator && node.times?.value);
    case 'rfm_component':
    case 'contact_list_component':
      return !!(node.field && node.operator && node.term?.length > 0);
    case 'customer_page_component':
      return isCustomerFieldValid(node);
    case 'affinity_page_component':
      return !!(node.field && node.affinity_type && node.operator && node.term);
    case 'country':
      return !!(node.field && node.operator && node.term?.length > 0);
    case 'likehood_component':
    case 'lifecycle_component':
      return !!(node.field && node.operator && node.term);
    case 'live_shopping_component':
      return !!(node.field && node.times?.operator && node.times?.value
        && isNestedChildrenValid(node, isAnyLiveShoppingField));
    case 'custom_interaction_component':
      return !!(node.field && node.times?.operator && node.times?.value
        && isNestedChildrenValid(node, isAnyProductField));
    case 'products_component':
      return !!(node.field && node.times?.operator && node.times?.value && node.times?.frequency
        && isProductsChildrenValid(node));
    case 'channel_reachability_component':
      return !!(node.field && node.operator && node.term);
    case 'feedback_component': {
      let isOk = node.term?.children?.length > 0;
      node.term?.children?.forEach((c) => {
        isOk = c.field && c.operator && c.term;
      });
      isOk = isOk && node.times?.operator && node.times?.value;
      if (node.field === 'feedback_nps') {
        node.term.children.forEach((c) => {
          if (c.field === 'value') {
            isOk = isOk && Number.isInteger(c.term) && c.term >= 1 && c.term <= 10;
          }
        });
      }
      return isOk;
    }
    case 'interaction_channel_engagement_component':
    case 'channel_engagement_component':
      return isEngagementQueryValid(node.term, node.times);
    case 'notification_url_clicked_component':
      return !!node.broadcastId;
    case 'journey_notification_url_clicked_component':
      return !!node.journeyId;
    case 'search_component':
      return !!(node.field && node.times?.operator && node.times?.value && node.times?.frequency
        && isNestedChildrenValid(node, isAnyProductField));
    default:
      return false;
  }
}

export function summarizeAudienceNode(node, fieldMap, context, t) {
  if (!node?.field) return null;

  const fieldDef = fieldMap[node.field];
  const component = fieldDef?.term?.component;

  switch (component) {
    case 'input_num':
      return summarizeInputNum(node, fieldDef, t);
    case 'channel_reachability_component':
      return node.operator && node.term ? summarizeChannelReachability(node, t) : null;
    case 'notification_url_clicked_component':
      return node.broadcastId ? summarizeNotificationUrlClicked(node, context, t, false) : null;
    case 'journey_notification_url_clicked_component':
      return node.journeyId ? summarizeNotificationUrlClicked(node, context, t, true) : null;
    case 'channel_engagement_component':
      return node.times?.value ? summarizeChannelEngagement(node, fieldDef, context, t) : null;
    case 'interaction_channel_engagement_component':
      return node.times?.value ? summarizeInteractionChannelEngagement(node, fieldDef, context, t) : null;
    case 'rfm_component':
      return node.term?.length ? summarizeRfmCluster(node, t) : null;
    case 'contact_list_component':
      return node.term?.length ? summarizeContactLists(node, t) : null;
    case 'country':
      return node.term?.length ? summarizeListField(node, fieldDef, t) : null;
    case 'customer_page_component':
      return node.customer_field ? summarizeCustomerField(node, t) : null;
    case 'affinity_page_component':
      return node.affinity_type && node.term ? summarizeAffinity(node, fieldDef, t) : null;
    case 'likehood_component':
      return node.term ? summarizeLikehoodPurchase(node, t) : null;
    case 'lifecycle_component':
      return node.term ? summarizeCustomerCycleStatus(node, t) : null;
    case 'products_component':
      return node.times?.value ? summarizeProductsInteraction(node, fieldDef, t) : null;
    case 'live_shopping_component':
      return node.times?.value ? summarizeLiveShoppingInteraction(node, fieldDef, t) : null;
    case 'feedback_component':
      return node.times?.value ? summarizeFeedbackInteraction(node, fieldDef, t) : null;
    case 'custom_interaction_component':
      return node.times?.value ? summarizeNestedInteraction(node, fieldDef, t) : null;
    case 'search_component':
      return node.times?.value ? summarizeSearchInteraction(node, fieldDef, t) : null;
    default:
      return `${t('audiences.audience_conditions_summary_generic_prefix')} ${bold(fieldDef?.label || node.field)}.`;
  }
}

export function getAudienceSummaryBullets(rules, fieldMap, context, t) {
  return collectRuleNodes(rules)
    .filter((node) => isAudienceNodeValid(node, fieldMap))
    .map((node) => summarizeAudienceNode(node, fieldMap, context, t))
    .filter(Boolean);
}
